import type { Task, TaskPolicy } from '../../api/types';
import { addDaysISO, diffDays, kyivHour, nextWorkingDayISO, todayISO } from '../../lib/dates';

export type DueState = 'none' | 'overdue' | 'today' | 'soon' | 'later' | 'done';

/** Стан строку задачі відносно сьогодні (за Києвом). */
export function dueState(task: Pick<Task, 'dueOn' | 'status'>, today = todayISO()): DueState {
  if (task.status === 'completed') return 'done';
  if (!task.dueOn) return 'none';
  if (task.dueOn < today) return 'overdue';
  if (task.dueOn === today) return 'today';
  if (diffDays(today, task.dueOn) <= 7) return 'soon';
  return 'later';
}

export const PRIORITY_LABELS: Record<Task['priority'], string> = {
  low: 'Низький',
  normal: 'Звичайний',
  high: 'Високий',
  critical: 'Критичний',
};

export const SUBTYPE_LABELS: Record<Task['subtype'], string> = {
  task: 'Задача',
  milestone: 'Віха',
  approval: 'Погодження',
};

export const APPROVAL_LABELS: Record<NonNullable<Task['approvalStatus']>, string> = {
  pending: 'Очікує рішення',
  approved: 'Погоджено',
  changes_requested: 'На доопрацювання',
  rejected: 'Відхилено',
};

export const APPROVAL_TONES = {
  pending: 'neutral',
  approved: 'success',
  changes_requested: 'warning',
  rejected: 'danger',
} as const;

export const SOURCE_LABELS: Record<Task['source'], string> = {
  ui: 'Вебзастосунок',
  api: 'API',
  form: 'Форма',
  email: 'Лист',
  chat: 'Чат',
  ai: 'ШІ',
  agent: 'Агент',
  kommo: 'Kommo',
  import: 'Імпорт',
  rule: 'Правило автоматизації',
};

/**
 * Правило «після 15:00»: доручення іншому працівнику після години lateAssignHour
 * пропонують ставити не раніше ніж на наступний робочий день.
 */
export function lateAssignSuggestion(policy: TaskPolicy, assigneeId: string | null, meId: string, dueOn: string | null): string | null {
  if (!assigneeId || assigneeId === meId) return null;
  if (kyivHour() < policy.lateAssignHour) return null;
  const today = todayISO();
  if (dueOn && dueOn > today) return null;
  return nextWorkingDayISO(today);
}

/** Типовий строк нової задачі з урахуванням правила «після 15:00». */
export function defaultDue(policy: TaskPolicy, assigneeId: string | null, meId: string): string {
  const today = todayISO();
  if (assigneeId && assigneeId !== meId && kyivHour() >= policy.lateAssignHour) return nextWorkingDayISO(today);
  return today;
}

/** Поля журналу змін → людські назви. */
export const FIELD_LABELS: Record<string, string> = {
  title: 'Назва',
  description: 'Опис',
  assigneeId: 'Виконавець',
  priority: 'Пріоритет',
  urgent: 'Терміново',
  subtype: 'Тип',
  startOn: 'Дата початку',
  dueOn: 'Строк',
  dueAt: 'Строк (час)',
  deadlineOn: 'Крайній строк',
  nextReviewOn: 'Наступна перевірка',
  estimateMinutes: 'Оцінка часу',
  customFields: 'Поля',
  status: 'Стан',
  approvalStatus: 'Погодження',
  projects: 'Проєкти',
};

export const ACTION_LABELS: Record<string, string> = {
  created: 'Створено задачу',
  updated: 'Змінено',
  completed: 'Завершено',
  reopened: 'Повторно відкрито',
  archived: 'Архівовано',
  moved: 'Переміщено на етап',
  approval_decided: 'Ухвалено рішення',
  added_to_project: 'Додано до проєкту',
  removed_from_project: 'Вилучено з проєкту',
  handover: 'Передано іншому працівнику',
};

export const STATUS_LABELS: Record<string, string> = { open: 'Відкрита', completed: 'Завершена' };

export function taskUrl(id: string) {
  return `${window.location.origin}/tasks/${id}`;
}

export function isValidDate(s: string | null | undefined) {
  return !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

export { addDaysISO };
